import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Trash2, Minus, Plus, ShoppingBag } from 'lucide-react'
import type { ApiError } from '@/lib/api'
import { api } from '@/lib/api'
import type { AppLang } from '@/lib/format'
import { dayShortLabel, formatDateTime, formatPrice, localizedName } from '@/lib/format'
import { haptic } from '@/lib/telegram'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import { useStoreTheme } from '@/lib/useStoreTheme'
import type { Cart } from '@/shared/types'

import { StorefrontPage, StorefrontCard, StorefrontEmptyState, StorefrontLoading } from '@/components/StorefrontPage'

function formatWhen(iso: string, lang: string) {
  return formatDateTime(iso, lang as AppLang, 'dd.MM HH:mm')
}

export function CartPage() {
  const lang = useAppStore((s) => s.lang)
  const setCartCount = useAppStore((s) => s.setCartCount)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const T = useT('cart')
  const common = useT('common')
  const daysLabel = dayShortLabel(lang)
  const theme = useStoreTheme()

  const { data: cart, isLoading, error: loadError } = useQuery<Cart>({
    queryKey: ['cart'],
    queryFn: () => api.get<Cart>('/cart'),
  })

  useEffect(() => {
    if (cart) setCartCount(cart.count)
  }, [cart, setCartCount])

  function invalidate() {
    return qc.invalidateQueries({ queryKey: ['cart'] })
  }

  const updateQty = useMutation<unknown, ApiError, { id: number; qty: number }>({
    mutationFn: ({ id, qty }) =>
      qty <= 0 ? api.delete(`/cart/${id}`) : api.patch(`/cart/${id}`, { qty }),
    onSuccess: () => { haptic('light'); return invalidate() },
  })

  const removeProduct = useMutation<unknown, ApiError, number>({
    mutationFn: (id) => api.delete(`/cart/${id}`),
    onSuccess: () => { haptic('medium'); return invalidate() },
  })

  const removeService = useMutation<unknown, ApiError, number>({
    mutationFn: (id) => api.delete(`/cart/service/${id}`),
    onSuccess: () => { haptic('medium'); return invalidate() },
  })

  const clearCart = useMutation<unknown, ApiError>({
    mutationFn: () => api.delete('/cart'),
    onSuccess: () => { haptic('medium'); return invalidate() },
  })

  const busy = updateQty.isPending || removeProduct.isPending || removeService.isPending || clearCart.isPending
  const mutationError = updateQty.error || removeProduct.error || removeService.error || clearCart.error

  if (loadError) return <p role="alert" className="p-4 text-red-600">{common.error}</p>
  if (isLoading) return <StorefrontLoading message={T.loading} />

  if (!cart || cart.count === 0) {
    return <StorefrontEmptyState icon={ShoppingBag} title={T.empty} />
  }

  const maxEta = cart.items
    .filter((i) => i.is_made_to_order)
    .reduce((m, i) => Math.max(m, i.production_days), 0)
  const hasMto = cart.items.some((i) => i.is_made_to_order)

  return (
    <StorefrontPage
      title={T.title}
      badge={cart.count}
      className="pb-36"
    >
      {mutationError && (
        <p role="alert" className="mb-3 text-xs text-rose-500 font-medium">
          {common.actionFailed}
        </p>
      )}

      {/* Product items */}
      <div className="space-y-3">
        {cart.items.map((item) => {
          const name = localizedName(lang, item.name_uk, item.name_en)
          return (
            <StorefrontCard key={item.cart_item_id} className="p-3.5">
              <div className="flex gap-3">
                {item.photo_url ? (
                  <img
                    src={item.photo_url}
                    alt={name}
                    className="h-16 w-16 rounded-xl object-cover shrink-0 bg-slate-900/40"
                  />
                ) : (
                  <div className={`h-16 w-16 rounded-xl shrink-0 flex items-center justify-center text-2xl ${
                    theme.isDarkTheme ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'
                  }`}>
                    📦
                  </div>
                )}
                <div className="flex-1 min-w-0 flex flex-col justify-between">
                  <div>
                    <p className={`font-semibold text-sm truncate ${theme.isDarkTheme ? 'text-[#f8f3ea]' : 'text-slate-900'}`}>{name}</p>
                    <p className={`text-xs font-bold mt-0.5 ${theme.isDarkTheme ? 'text-amber-400' : 'text-brand-600'}`}>
                      {formatPrice(item.line_total_uah)}
                    </p>
                    {item.is_made_to_order && (
                      <p className={`text-[11px] mt-0.5 ${theme.isDarkTheme ? 'text-slate-400' : 'text-slate-400'}`}>
                        ⏳{' '}
                        {item.production_days
                          ? `${T.mtoSingle} · ${
                              item.production_days === 28
                                ? lang === 'en'
                                  ? '~4 weeks'
                                  : '~4 тижні'
                                : `~${item.production_days} ${daysLabel}`
                            }`
                          : T.mtoSingle}
                      </p>
                    )}
                  </div>
                  <div className={`flex items-center justify-between mt-2 pt-1 border-t ${
                    theme.isDarkTheme ? 'border-[#3a4352]/70' : 'border-slate-100'
                  }`}>
                    <div className={`flex items-center gap-1.5 shrink-0 rounded-lg p-0.5 border ${
                      theme.isDarkTheme ? 'bg-[#1a202c] border-[#3a4352]' : 'bg-slate-50 border-slate-200/60'
                    }`}>
                      <button
                        aria-label={`${T.decrease}: ${name}`}
                        disabled={busy}
                        onClick={() => { haptic(); updateQty.mutate({ id: item.cart_item_id, qty: item.qty - 1 }) }}
                        className={`flex h-6 w-6 items-center justify-center rounded-md shadow-2xs transition disabled:opacity-50 ${
                          theme.isDarkTheme
                            ? 'bg-[#242c38] text-slate-200 hover:bg-[#2f3948]'
                            : 'bg-white text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className={`w-6 text-center text-xs font-bold ${
                        theme.isDarkTheme ? 'text-[#f8f3ea]' : 'text-slate-900'
                      }`}>{item.qty}</span>
                      <button
                        aria-label={`${T.increase}: ${name}`}
                        disabled={busy}
                        onClick={() => { haptic(); updateQty.mutate({ id: item.cart_item_id, qty: item.qty + 1 }) }}
                        className={`flex h-6 w-6 items-center justify-center rounded-md shadow-2xs transition disabled:opacity-50 ${
                          theme.isDarkTheme
                            ? 'bg-[#242c38] text-slate-200 hover:bg-[#2f3948]'
                            : 'bg-white text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <button
                      aria-label={`${T.remove}: ${name}`}
                      disabled={busy}
                      onClick={() => removeProduct.mutate(item.cart_item_id)}
                      className={`p-1 disabled:opacity-50 transition ${
                        theme.isDarkTheme ? 'text-slate-400 hover:text-rose-400' : 'text-slate-400 hover:text-rose-600'
                      }`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </StorefrontCard>
          )
        })}

        {/* Service items */}
        {cart.service_items.map((item) => {
          const name = localizedName(lang, item.name_uk, item.name_en)
          return (
            <StorefrontCard key={`svc-${item.cart_item_id}`} className="p-3.5">
              <div className="flex gap-3">
                <div className={`h-16 w-16 rounded-xl shrink-0 flex items-center justify-center text-2xl border ${
                  theme.isDarkTheme
                    ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                    : 'bg-brand-50 border-brand-100'
                }`}>
                  📅
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-between">
                  <div>
                    <p className={`font-semibold text-sm truncate ${theme.isDarkTheme ? 'text-[#f8f3ea]' : 'text-slate-900'}`}>{name}</p>
                    <p className={`text-xs font-bold mt-0.5 ${theme.isDarkTheme ? 'text-amber-400' : 'text-brand-600'}`}>
                      {formatPrice(item.line_total_uah)}
                    </p>
                    <p className={`text-[11px] mt-0.5 ${theme.isDarkTheme ? 'text-slate-300' : 'text-slate-500'}`}>
                      {item.provider_name} · {formatWhen(item.starts_at, lang)}
                    </p>
                    {item.customer_notes && (
                      <p className={`text-[11px] truncate ${theme.isDarkTheme ? 'text-slate-400' : 'text-slate-400'}`}>{item.customer_notes}</p>
                    )}
                  </div>
                  <div className={`flex justify-end pt-1 border-t mt-2 ${
                    theme.isDarkTheme ? 'border-[#3a4352]/70' : 'border-slate-100'
                  }`}>
                    <button
                      aria-label={`${T.remove}: ${name}`}
                      disabled={busy}
                      onClick={() => removeService.mutate(item.cart_item_id)}
                      className={`p-1 disabled:opacity-50 transition ${
                        theme.isDarkTheme ? 'text-slate-400 hover:text-rose-400' : 'text-slate-400 hover:text-rose-600'
                      }`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </StorefrontCard>
          )
        })}
      </div>

      {/* MTO banner */}
      {hasMto && (
        <div className={`mt-3 rounded-xl px-4 py-2.5 text-xs font-medium shadow-xs ${
          theme.isDarkTheme
            ? 'bg-amber-500/15 border border-amber-500/30 text-amber-300'
            : 'bg-gradient-to-r from-brand-600 to-brand-700 text-white'
        }`}>
          {maxEta ? T.mtoEta(maxEta) : T.mtoGeneric}
        </div>
      )}

      {/* Total Card */}
      <StorefrontCard className="mt-3 p-4">
        <div className="flex items-center justify-between">
          <span className={`text-sm font-medium ${theme.isDarkTheme ? 'text-slate-300' : 'text-slate-600'}`}>{T.total}:</span>
          <span className={`text-lg font-bold ${theme.isDarkTheme ? 'text-[#f8f3ea]' : 'text-slate-900'}`}>{formatPrice(cart.total_uah)}</span>
        </div>
      </StorefrontCard>

      {/* Sticky footer */}
      <div className={`app-bottom-fixed app-bottom-panel flex gap-2 border-t backdrop-blur-md p-3 shadow-lg ${
        theme.isDarkTheme ? 'bg-[#1a202c]/95 border-[#2d3748]' : 'bg-white/95 border-slate-200/80'
      }`}>
        <button
          onClick={() => clearCart.mutate()}
          disabled={busy}
          className={`rounded-xl border active:scale-95 px-4 py-2.5 text-xs font-semibold transition disabled:opacity-50 ${
            theme.isDarkTheme
              ? 'border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400'
              : 'border-rose-200 bg-rose-50/50 hover:bg-rose-100 text-rose-600'
          }`}
        >
          {T.clear}
        </button>
        <button
          disabled={busy}
          onClick={() => void navigate({ to: '/checkout' })}
          className={`flex-1 rounded-xl active:scale-95 py-2.5 text-xs font-bold shadow-md transition disabled:opacity-50 ${
            theme.isDarkTheme ? theme.accentBtnClass : 'bg-brand-600 hover:bg-brand-700 text-white'
          }`}
        >
          {T.checkout(cart.total_uah)}
        </button>
      </div>
    </StorefrontPage>
  )
}
